import {
  Activity,
  ArrowRight,
  BarChart3,
  BookOpen,
  Bot,
  Check,
  Database,
  Eye,
  LayoutDashboard,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { useI18n } from "../i18n";
import { SectionHeading } from "./ui/card";
import { Reveal } from "./reveal";

const BARRAS = [32, 48, 40, 62, 54, 70, 58, 78, 66, 88, 74, 96];

/** Mock ilustrativo do dashboard da Central (números fictícios) —
    janela clara, hairlines e tinta, como o resto do site. */
function DashboardMock() {
  const { t } = useI18n();
  const stats = [
    { label: t("central.p.s1"), valor: "14" },
    { label: t("central.p.s2"), valor: "715" },
    { label: t("central.p.s3"), valor: "2.163" },
  ];
  const sideIcons = [
    { icon: LayoutDashboard, rotulo: "Visão geral", ativo: true },
    { icon: Bot, rotulo: "Agentes", ativo: false },
    { icon: MessageCircle, rotulo: "Atendimento", ativo: false },
    { icon: BookOpen, rotulo: "Conhecimento", ativo: false },
    { icon: BarChart3, rotulo: "Auditoria", ativo: false },
  ];

  return (
    <div className="relative overflow-hidden rounded-xl border border-line bg-surface shadow-[0_2px_4px_rgb(26_25_21/0.04),0_28px_56px_-24px_rgb(26_25_21/0.22)]">
      {/* barra da janela */}
      <div className="flex items-center gap-2 border-b border-line bg-surface-2/60 px-4 py-3">
        <span className="size-2.5 rounded-full border border-line bg-background" />
        <span className="size-2.5 rounded-full border border-line bg-background" />
        <span className="size-2.5 rounded-full border border-line bg-background" />
        <span className="ml-2 rounded border border-line bg-surface px-2.5 py-1 font-mono text-[10px] text-subtle">
          central.vortolabs.com
        </span>
      </div>

      <div className="grid grid-cols-[44px_1fr] sm:grid-cols-[52px_1fr]">
        {/* sidebar */}
        <div className="flex flex-col items-center gap-1.5 border-r border-line py-4">
          {sideIcons.map(({ icon: Icon, rotulo, ativo }) => (
            <span
              key={rotulo}
              title={rotulo}
              className={
                ativo
                  ? "grid size-8 place-items-center rounded-lg bg-accent-soft text-accent"
                  : "grid size-8 place-items-center rounded-lg text-subtle transition-colors hover:text-muted-foreground"
              }
            >
              <Icon className="size-4" strokeWidth={1.8} />
            </span>
          ))}
        </div>

        {/* conteúdo */}
        <div className="flex flex-col gap-4 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-mono text-[10px] tracking-wider text-subtle uppercase">
              {t("central.p.badge")}
            </span>
            <span className="flex items-center gap-1.5 rounded-full border border-accent/20 bg-accent-soft px-2.5 py-1 text-[10px] font-medium text-accent">
              <span className="animate-pulse-dot size-1.5 rounded-full bg-accent" />
              {t("central.p.online")}
            </span>
          </div>

          <div>
            <p className="font-display text-base font-medium tracking-tight sm:text-lg">
              {t("central.p.hello")}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("central.p.live")}</p>
          </div>

          {/* stats */}
          <div className="grid grid-cols-3 gap-2.5">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg border border-line bg-background px-3 py-2.5">
                <p className="truncate text-[10px] leading-tight text-subtle">{s.label}</p>
                <p className="mt-1 font-mono text-lg font-medium text-foreground tabular-nums sm:text-xl">
                  {s.valor}
                </p>
              </div>
            ))}
          </div>

          {/* gráfico de barras decorativo */}
          <div className="flex h-20 items-end gap-1.5 rounded-lg border border-line bg-background px-3 pb-2.5 pt-3 sm:h-24">
            {BARRAS.map((h, i) => (
              <span
                key={i}
                className="w-full rounded-sm bg-accent/75"
                style={{ height: `${h}%`, opacity: 0.45 + (h / 96) * 0.55 }}
              />
            ))}
          </div>

          {/* agentes */}
          <div className="flex flex-col gap-2">
            {[
              { nome: t("central.p.r1"), tag: "WhatsApp", valor: "96%" },
              { nome: t("central.p.r2"), tag: "API", valor: "US$ 71,97" },
            ].map((r) => (
              <div
                key={r.nome}
                className="flex items-center gap-3 rounded-lg border border-line bg-background px-3 py-2.5"
              >
                <span className="animate-pulse-dot size-1.5 shrink-0 rounded-full bg-accent" />
                <span className="min-w-0 flex-1 truncate text-xs text-foreground/85">{r.nome}</span>
                <span className="rounded border border-accent/20 bg-accent-soft px-1.5 py-0.5 font-mono text-[9px] text-accent">
                  {r.tag}
                </span>
                <span className="font-mono text-xs text-muted-foreground tabular-nums">{r.valor}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Card flutuante: aprovação humana — o "você no controle" da Central. */
function ApprovalCard() {
  const { t } = useI18n();
  return (
    <div className="absolute -bottom-7 -left-3 w-60 rotate-[-2deg] rounded-lg border border-line bg-surface p-3.5 shadow-[0_2px_4px_rgb(26_25_21/0.05),0_20px_40px_-16px_rgb(26_25_21/0.28)] sm:-left-8 sm:w-64">
      <div className="flex items-center gap-2">
        <span className="grid size-6 place-items-center rounded bg-amber-mark/25 text-amber-700">
          <Eye className="size-3.5" />
        </span>
        <p className="text-[11px] leading-snug font-medium text-foreground/90">
          {t("central.p.fl.t")}
        </p>
      </div>
      <div className="mt-2.5 flex gap-2">
        <span className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-primary py-1.5 text-[11px] font-semibold text-white">
          <Check className="size-3" strokeWidth={3} />
          {t("central.p.fl.a")}
        </span>
        <span className="inline-flex flex-1 items-center justify-center rounded-md border border-line py-1.5 text-[11px] font-medium text-muted-foreground">
          {t("central.p.fl.r")}
        </span>
      </div>
    </div>
  );
}

/* Identidade do Boardyz: o "prisma" violeta → magenta → âmbar. */
const PRISMA = "linear-gradient(135deg, #7B3FE4 0%, #C42A93 55%, #E2603C 100%)";

function BoardyzIcon({ className }: { className?: string }) {
  return (
    <span
      className={"grid shrink-0 place-items-center rounded-lg " + (className ?? "size-10")}
      style={{ background: PRISMA }}
      aria-hidden
    >
      <span className="flex items-end gap-[3px]">
        <span className="h-2.5 w-1 rounded-full bg-white" />
        <span className="h-4 w-1 rounded-full bg-white" />
        <span className="h-3 w-1 rounded-full bg-white" />
      </span>
    </span>
  );
}

/** Mock ilustrativo do Boardyz — mini kanban com um card sendo movido pela IA. */
function BoardyzMock() {
  const colunas = [
    {
      titulo: "A fazer",
      cards: [
        { t: "Proposta comercial Q3", labels: ["#7B3FE4"] },
        { t: "Revisar contrato", labels: ["#C42A93", "#E2603C"] },
      ],
    },
    {
      titulo: "Fazendo",
      cards: [{ t: "Onboarding do cliente", labels: ["#C42A93"], avatar: "L" }],
    },
    {
      titulo: "Feito",
      cards: [{ t: "Publicar landing", labels: ["#7B3FE4"], ia: true }],
    },
  ];
  return (
    <div className="relative">
      <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-[0_2px_4px_rgb(26_25_21/0.04),0_28px_56px_-24px_rgb(26_25_21/0.22)]">
        {/* barra da janela */}
        <div className="flex items-center gap-2 border-b border-line bg-surface-2/60 px-4 py-3">
          <span className="size-2.5 rounded-full border border-line bg-background" />
          <span className="size-2.5 rounded-full border border-line bg-background" />
          <span className="size-2.5 rounded-full border border-line bg-background" />
          <span className="ml-2 rounded border border-line bg-surface px-2.5 py-1 font-mono text-[10px] text-subtle">
            boardyz.com
          </span>
        </div>
        {/* colunas */}
        <div className="grid grid-cols-3 gap-3 p-4 sm:p-5">
          {colunas.map((col) => (
            <div key={col.titulo} className="flex flex-col gap-2">
              <p className="font-mono text-[10px] tracking-wider text-subtle uppercase">
                {col.titulo}
              </p>
              {col.cards.map((c) => (
                <div
                  key={c.t}
                  className="rounded-lg border border-line bg-background p-2.5 shadow-[0_1px_2px_rgb(26_25_21/0.04)]"
                >
                  <div className="flex gap-1">
                    {c.labels.map((cor) => (
                      <span key={cor} className="h-1.5 w-6 rounded-full" style={{ background: cor }} />
                    ))}
                  </div>
                  <p className="mt-1.5 text-[11px] leading-snug text-foreground/85">{c.t}</p>
                  {"avatar" in c && c.avatar && (
                    <span className="mt-1.5 grid size-5 place-items-center rounded-full bg-accent-soft text-[9px] font-semibold text-accent">
                      {c.avatar}
                    </span>
                  )}
                  {"ia" in c && c.ia && (
                    <span
                      className="mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[9px] text-white"
                      style={{ background: PRISMA }}
                    >
                      <Bot className="size-2.5" /> via MCP
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {/* card flutuante: a IA operando o quadro */}
      <div className="absolute -bottom-6 -right-2 w-56 rotate-[2deg] rounded-lg border border-line bg-surface p-3 shadow-[0_2px_4px_rgb(26_25_21/0.05),0_20px_40px_-16px_rgb(26_25_21/0.28)] sm:-right-6">
        <p className="flex items-center gap-2 text-[11px] leading-snug text-foreground/90">
          <span className="grid size-6 shrink-0 place-items-center rounded" style={{ background: PRISMA }}>
            <Bot className="size-3.5 text-white" />
          </span>
          <span>
            <span className="font-semibold">Claude</span> moveu{" "}
            <span className="font-medium">“Publicar landing”</span> → Feito
          </span>
        </p>
      </div>
    </div>
  );
}

/** Seção Produtos — a Central (carro-chefe) + Boardyz. */
export function Products() {
  const { t } = useI18n();
  const items = [
    {
      icon: Database,
      titulo: t("central.i1.t"),
      desc: t("central.i1.d"),
    },
    {
      icon: Activity,
      titulo: t("central.i2.t"),
      desc: t("central.i2.d"),
    },
    {
      icon: ShieldCheck,
      titulo: t("central.i3.t"),
      desc: t("central.i3.d"),
    },
  ];

  return (
    <section id="produtos" className="relative scroll-mt-24 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <SectionHeading
            index="01"
            label={t("prod.rotulo")}
            title={t("prod.titulo")}
            description={t("prod.desc")}
            className="mb-14 sm:mb-20"
          />
        </Reveal>

        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-14">
          {/* texto */}
          <div className="flex flex-col items-start gap-5">
            <Reveal>
              <p className="flex items-center gap-2.5 font-mono text-[11px] font-medium tracking-[0.16em] uppercase">
                <span className="grid size-6 place-items-center rounded-md bg-accent text-white normal-case">
                  <span className="font-sans text-[11px] font-bold">C</span>
                </span>
                <span className="text-accent">{t("central.rotulo")}</span>
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-[clamp(1.9rem,4.5vw,3rem)] leading-[1.08] font-semibold tracking-[-0.02em] text-balance">
                {t("central.titulo")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg text-pretty">
                {t("central.desc")}
              </p>
            </Reveal>
            <ul className="mt-2 flex flex-col gap-0 border-t border-line">
              {items.map((item, i) => (
                <Reveal key={item.titulo} delay={0.12 + i * 0.06}>
                  <li className="flex gap-4 border-b border-line py-5">
                    <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent">
                      <item.icon className="size-[18px]" strokeWidth={1.8} />
                    </span>
                    <div>
                      <h3 className="font-display text-[16px] font-medium tracking-tight">
                        {item.titulo}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          {/* dashboard */}
          <Reveal delay={0.15} y={32} className="relative pb-10 pl-3 sm:pl-6">
            <div className="relative">
              <DashboardMock />
              <ApprovalCard />
            </div>
          </Reveal>
        </div>

        {/* ---- Boardyz: o segundo produto ---- */}
        <div className="mt-20 grid items-center gap-16 border-t border-line pt-16 sm:mt-24 sm:pt-20 lg:grid-cols-2 lg:gap-14">
          {/* mock à esquerda (espelha o bloco da Central) */}
          <Reveal y={32} className="relative order-2 pb-8 pr-3 sm:pr-6 lg:order-1">
            <BoardyzMock />
          </Reveal>

          {/* texto */}
          <div className="order-1 flex flex-col items-start gap-5 lg:order-2">
            <Reveal>
              <p className="flex items-center gap-2.5 font-mono text-[11px] font-medium tracking-[0.16em] uppercase">
                <BoardyzIcon className="size-6 rounded-md" />
                <span className="text-foreground/70">{t("bz.chip")}</span>
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-[clamp(1.9rem,4.5vw,3rem)] leading-[1.08] font-semibold tracking-[-0.02em] text-balance">
                {t("bz.titulo")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg text-pretty">
                {t("bz.desc")}
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <ul className="flex flex-col gap-2.5">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-center gap-2.5 text-sm text-foreground/80">
                    <span className="size-1 rounded-full" style={{ background: "#C42A93" }} />
                    {t(`bz.li${n}`)}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <a
                href="#contato"
                className="mt-1 inline-flex items-center gap-2 border-b border-foreground/20 pb-0.5 text-sm font-medium text-foreground transition-colors hover:border-accent hover:text-accent"
              >
                {t("bz.cta")}
                <ArrowRight className="size-4" />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
